import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import API from './api';
import { consumeDccBootstrapIfPresent } from './dccBootstrap';
import { REVOCATION_ENABLED, startHeartbeat, subscribe, broadcastRevoked } from './sessionRevocation';
const AuthContext = createContext(null);
export function AuthProvider({ children }) {
  const [user,         setUser]         = useState(null);
  const [accountState, setAccountState] = useState(null);
  const [loading,      setLoading]      = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // GENERAL DCC hosted-web bootstrap: if the installed DCC opened this page in its dedicated
      // product window with a single-use ?dcc= token, exchange it for the normal VISINVOKE HttpOnly
      // web session cookie BEFORE the authoritative /me check (the token is then stripped from the
      // URL). Fail-closed: on any error we simply fall through to the normal /me + interceptor path,
      // so a genuinely unauthenticated visit still redirects to /login. No second login is required
      // when the bootstrap succeeds.
      await consumeDccBootstrapIfPresent();
      try {
        const r = await API.get('/v1/auth/me');
        if (!cancelled) {
          setUser(r.data.user);
          setAccountState(r.data.account_state || null);
        }
      } catch (e) {
        // Not authenticated or session expired.
        // api.js interceptor handles 401 redirect to /login.
        // 403 account_locked/account_restricted handled by interceptor.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Re-fetch the authoritative account (used after an account-state change such as
  // recording the DCC download) so the dashboard reads fresh onboarding data.
  const refreshUser = useCallback(async () => {
    try {
      const r = await API.get('/v1/auth/me');
      setUser(r.data.user);
      return r.data.user;
    } catch {
      return null;
    }
  }, []);

  // --- Session revocation: shared customer check ---------------------------
  // Verifies the session against the server. The existing api.js interceptor
  // owns the 401 -> /login redirect and the public-path allowlist (api.js is
  // untouched). Classifies ONLY explicit 401/403 as 'revoked'; network / 5xx
  // are 'transient' and never log the user out.
  const checkSession = useCallback(async () => {
    try {
      await API.get('/v1/auth/me');
      return 'ok';
    } catch (err) {
      const status = err && err.response ? err.response.status : undefined;
      return (status === 401 || status === 403) ? 'revoked' : 'transient';
    }
  }, []);

  // Re-verify on demand (used by the broadcast listener). Single-flight so a
  // burst of cross-tab events cannot stack /me calls. Clears local state only
  // on an explicit revocation; the interceptor performs the redirect.
  const revalidatingRef = useRef(false);
  const revalidate = useCallback(async () => {
    if (revalidatingRef.current) return;
    revalidatingRef.current = true;
    try {
      const result = await checkSession();
      if (result === 'revoked') {
        setUser(null);
        setAccountState(null);
      }
    } finally {
      revalidatingRef.current = false;
    }
  }, [checkSession]);

  // Track auth state for the (mount-once) broadcast listener without resubscribing.
  const authedRef = useRef(false);
  const isAuthed = !!user;
  useEffect(() => { authedRef.current = isAuthed; }, [isAuthed]);

  // --- Session revocation: customer heartbeat (Batch 1) --------------------
  // Runs ONLY while a customer user is authenticated. StrictMode-safe: each
  // startHeartbeat() returns its own stop() which cleanup calls, leaving exactly
  // one live heartbeat.
  useEffect(() => {
    if (!REVOCATION_ENABLED || !isAuthed) return undefined;
    const stop = startHeartbeat({
      scope: 'customer',
      check: checkSession,
      onRevoked: () => {
        setUser(null);
        setAccountState(null);
      },
    });
    return () => { stop(); };
  }, [isAuthed, checkSession]);

  // --- Session revocation: customer broadcast listener (Batch 2) -----------
  // Reacts to same-origin cross-tab events. Only 'customer'/'all' scopes apply
  // here (a 'staff'-scoped event never affects a customer tab). On either a
  // hard 'session-revoked' or a 'session-revalidate', the tab re-verifies with
  // the server: a kept session (200) stays; a revoked one (401/403) clears state
  // and the api.js interceptor redirects to /login. Self-emitted events are
  // suppressed inside sessionRevocation.js. StrictMode-safe (subscribe/unsub).
  useEffect(() => {
    if (!REVOCATION_ENABLED) return undefined;
    const unsubscribe = subscribe((msg) => {
      if (!msg || !authedRef.current) return; // ignore when not authenticated
      const s = msg.scope;
      if (s !== 'customer' && s !== 'all') return;
      if (msg.type === 'session-revoked' || msg.type === 'session-revalidate') {
        revalidate();
      }
    });
    return () => { unsubscribe(); };
  }, [revalidate]);

  const login = async (email, password) => {
    const r = await API.post('/v1/auth/login', { email, password });
    setUser(r.data.user);
    setAccountState(null);
    sessionStorage.removeItem('suspension_reason');
    sessionStorage.removeItem('lock_state');
    localStorage.removeItem('visinvoke_token');
    localStorage.removeItem('token');
    return r.data;
  };

  const register = async (email, password, firstName, lastName, referralCode, dccSelected, continuation) => {
    const r = await API.post('/v1/auth/register', {
      email,
      password,
      first_name:    firstName,
      last_name:     lastName,
      referral_code: referralCode || undefined,
      dcc_selected:  !!dccSelected,
      continuation:  continuation || undefined,
    });
    setUser(r.data.user);
    setAccountState(null);
    sessionStorage.removeItem('suspension_reason');
    sessionStorage.removeItem('lock_state');
    localStorage.removeItem('visinvoke_token');
    localStorage.removeItem('token');
    return r.data;
  };

  const logout = async () => {
    try {
      await API.post('/v1/auth/logout');
    } catch {
      // Ignore
    }
    localStorage.removeItem('visinvoke_token');
    localStorage.removeItem('token');
    sessionStorage.removeItem('suspension_reason');
    sessionStorage.removeItem('lock_state');
    setUser(null);
    setAccountState(null);
    // Batch 2: tell sibling same-origin tabs the shared cookie is gone. Customer
    // logout clears the .visinvoke.com auth_token cookie, so every same-browser
    // tab (customer and staff) loses auth -> scope 'all'.
    broadcastRevoked({ reason: 'logout', scope: 'all' });
  };

  return (
    <AuthContext.Provider value={{ user, accountState, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}
export const useAuth = () => useContext(AuthContext);
